import { gql, useQuery } from "@apollo/client";
import { Card, Grid } from "@mui/material";
import logo from "assets/images/comedor_alt.png";
import menuIcon from "assets/images/icons/menu.png";
import sinTacc from "assets/images/icons/sin-tacc.png";
import vegetariano from "assets/images/icons/vegetariano.png";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import dayjs from "dayjs";
import { useEffect, useRef } from "react"; // Añadir useRef
import { getUproColor } from "utils/colors";

function FixedShapesBackground() {
  const canvasRef = useRef(null);
  const shapesRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let w = (canvas.width = window.innerWidth);
    let h = (canvas.height = window.innerHeight);
    let animationFrameId;

    const defineShapes = (currentW, currentH) => [
      {
        type: "circle",
        color: "#67C1B8",
        x: currentW * 0.15,
        y: currentH * 0.2,
        size: Math.min(currentW, currentH) * 0.15,
        angle: Math.PI / 6,
        animOffset: Math.random() * 10,
        animSpeed: 0.0005,
        animMagnitude: Math.min(currentW, currentH) * 0.015,
      },
      {
        type: "donut",
        color: "#F59B24",
        x: currentW * 0.8,
        y: currentH * 0.3,
        size: Math.min(currentW, currentH) * 0.22,
        angle: -Math.PI / 4,
        animOffset: Math.random() * 10,
        animSpeed: 0.0006,
        animMagnitude: Math.min(currentW, currentH) * 0.01,
      },
      {
        type: "donut",
        color: "#F59B24",
        x: currentW * 0,
        y: currentH * 0.12,
        size: Math.min(currentW, currentH) * 0.22,
        angle: -Math.PI / 4,
        animOffset: Math.random() * 10,
        animSpeed: 0.0006,
        animMagnitude: Math.min(currentW, currentH) * 0.01,
      },
      {
        type: "donut",
        color: "#F59B24",
        x: currentW * 0.23,
        y: currentH * 1,
        size: Math.min(currentW, currentH) * 0.22,
        angle: -Math.PI / 4,
        animOffset: Math.random() * 10,
        animSpeed: 0.0006,
        animMagnitude: Math.min(currentW, currentH) * 0.01,
      },
      {
        type: "x",
        color: "#ffffff",
        x: currentW * 0.75,
        y: currentH * 0.8,
        size: Math.min(currentW, currentH) * 0.13,
        angle: 0,
        animOffset: Math.random() * 10,
        animSpeed: 0.001,
        animMagnitude: Math.min(currentW, currentH) * 0.006,
      },
      {
        type: "x",
        color: "#ffffff",
        x: currentW * 0.13,
        y: currentH * 0.9,
        size: Math.min(currentW, currentH) * 0.05,
        angle: 130 * (Math.PI / 180),
        animOffset: Math.random() * 10,
        animSpeed: 0.001,
        animMagnitude: Math.min(currentW, currentH) * 0.006,
      },
      {
        type: "x",
        color: "#ffffff",
        x: currentW * 0.19,
        y: currentH * 0.23,
        size: Math.min(currentW, currentH) * 0.04,
        angle: 45 * (Math.PI / 180),
        animOffset: Math.random() * 10,
        animSpeed: 0.001,
        animMagnitude: Math.min(currentW, currentH) * 0.006,
      },
      {
        type: "circle",
        color: "#67C1B8",
        x: currentW * 1,
        y: currentH * 0.1,
        size: Math.min(currentW, currentH) * 0.16,
        angle: 0,
        animOffset: Math.random() * 10,
        animSpeed: 0.0007,
        animMagnitude: Math.min(currentW, currentH) * 0.006,
      },
      {
        type: "circle",
        color: "#67C1B8",
        x: currentW * 0,
        y: currentH * 1,
        size: Math.min(currentW, currentH) * 0.35,
        angle: 0,
        animOffset: Math.random() * 20,
        animSpeed: 0.001,
        animMagnitude: Math.min(currentW, currentH) * 0.006,
      },
      {
        type: "donut",
        color: "#F59B24",
        x: currentW * 0.5,
        y: currentH * 0.1,
        size: Math.min(currentW, currentH) * 0.09,
        angle: Math.PI / 3,
        animOffset: Math.random() * 10,
        animSpeed: 0.0005,
        animMagnitude: Math.min(currentW, currentH) * 0.005,
      },
      {
        type: "donut",
        color: "#F59B24",
        x: currentW * 1,
        y: currentH * 1,
        size: Math.min(currentW, currentH) * 0.5,
        angle: Math.PI / 3,
        animOffset: Math.random() * 10,
        animSpeed: 0.0015,
        animMagnitude: Math.min(currentW, currentH) * 0.005,
      },
    ];

    shapesRef.current = defineShapes(w, h);

    const animate = (time) => {
      ctx.clearRect(0, 0, w, h);

      shapesRef.current.forEach((s) => {
        const currentY = s.y + Math.sin(time * s.animSpeed + s.animOffset) * s.animMagnitude;

        // Animación de shake para 'x'
        const angleShake =
          s.type === "x" ? Math.sin(time * s.animSpeed * 2 + s.animOffset) * (Math.PI / 32) : 0;

        ctx.save();
        ctx.translate(s.x, currentY);
        ctx.rotate(s.angle + angleShake);
        ctx.strokeStyle = s.color;
        ctx.fillStyle = s.color;
        ctx.lineWidth = Math.max(3, s.size / 25);
        ctx.globalAlpha = 1;

        switch (s.type) {
          case "circle":
            ctx.beginPath();
            ctx.arc(0, 0, s.size / 2, 0, 2 * Math.PI);
            ctx.fill();
            break;
          case "donut":
            const outerRadius = s.size / 2;
            const innerRadius = s.size / 4;
            ctx.beginPath();
            ctx.arc(0, 0, outerRadius, 0, 2 * Math.PI);
            ctx.fill();
            if (innerRadius > 0) {
              ctx.globalCompositeOperation = "destination-out";
              ctx.beginPath();
              ctx.arc(0, 0, innerRadius, 0, Math.PI * 2);
              ctx.fill();
              ctx.globalCompositeOperation = "source-over";
            }
            break;
          case "x":
            const maxLength = s.size / Math.SQRT2;
            const currentLength = maxLength / 2;
            ctx.beginPath();
            ctx.moveTo(-currentLength, -currentLength);
            ctx.lineTo(currentLength, currentLength);
            ctx.moveTo(currentLength, -currentLength);
            ctx.lineTo(-currentLength, currentLength);
            ctx.stroke();
            break;
        }

        ctx.restore();
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);

    const handleResize = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
      shapesRef.current = defineShapes(w, h);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        zIndex: 0,
        pointerEvents: "none",
      }}
    />
  );
}

const GET_MENUS = gql`
  query getMenusDelDia {
    menusDelDiaPublic
  }
`;

function TvMenuDiario() {
  const handleContextMenu = (e) => e.preventDefault();

  // → Polling cada 3600000ms = 1 hora
  const { data } = useQuery(GET_MENUS, {
    pollInterval: 1000 * 60 * 60,
  });

  const hour = dayjs().hour();
  const turnoActual = hour >= 16 ? "Noche" : "Mediodía";

  const menusFiltrados = data?.menusDelDiaPublic?.filter((m) => m.turno === turnoActual) || [];

  return (
    <SoftBox
      onContextMenu={handleContextMenu}
      sx={{
        userSelect: "none",
        position: "relative",
        overflow: "hidden",
        height: "100vh",
        backgroundColor: getUproColor("uproPink"),
      }}
    >
      <FixedShapesBackground />

      <SoftBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
        sx={{ position: "relative", zIndex: 1, p: { xs: 2, md: 4 } }}
      >
        <SoftBox sx={{ width: "90%", maxWidth: "1200px" }}>
          <SoftBox display="flex" justifyContent="center" alignItems="center">
            <SoftBox component="img" src={logo} alt="UPrO Logo" width="15rem" p={3} />
          </SoftBox>

          <Grid container spacing={3} justifyContent="center" alignItems="stretch">
            {menusFiltrados.map((menuItem, idx) => (
              <Grid item xs={12} sm={6} md={4} key={menuItem.nombre + idx}>
                <Card sx={{ height: "100%" }}>
                  <SoftBox p={3} textAlign="center">
                    <SoftBox
                      component="img"
                      src={
                        menuItem?.producto?.tipoProducto?.tipo === "Vegetariano"
                          ? vegetariano
                          : menuItem?.producto?.tipoProducto?.tipo === "Libre de gluten"
                          ? sinTacc
                          : menuIcon
                      }
                      alt="Icono menú"
                      width="10rem"
                      p={3}
                      sx={{
                        animation: "float 3s ease-in-out infinite",
                        "@keyframes float": {
                          "0%": { transform: "translateY(0)" },
                          "50%": { transform: "translateY(-10px)" },
                          "100%": { transform: "translateY(0)" },
                        },
                      }}
                    />
                    <SoftTypography variant="h4" fontWeight="bold" gutterBottom mt={1}>
                      {menuItem.nombre}
                    </SoftTypography>
                  </SoftBox>
                </Card>
              </Grid>
            ))}
          </Grid>
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );
}

export default TvMenuDiario;
